import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ExternalLink } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getDSRDetail, type DSRDetailData } from '@/services/db/dsrDetail';
import RiskScoreCard from '@/components/dsr/RiskScoreCard';
import DailySetupSection from '@/components/dsr/DailySetupSection';
import WorkforceSection from '@/components/dsr/WorkforceSection';
import ActivitiesSection from '@/components/dsr/ActivitiesSection';
import EquipmentSection from '@/components/dsr/EquipmentSection';
import HSEObservationsSection from '@/components/dsr/HSEObservationsSection';
import PermitsSection from '@/components/dsr/PermitsSection';
import ShiftBriefCard from '@/components/dsr/ShiftBriefCard';
import SafetyMeetingsSection from '@/components/dsr/SafetyMeetingsSection';
import JSAEvaluationsSection from '@/components/dsr/JSAEvaluationsSection';
import SWAEventsSection from '@/components/dsr/SWAEventsSection';
import SafetyEventsSection from '@/components/dsr/SafetyEventsSection';
import ForecastingSection from '@/components/dsr/ForecastingSection';
import NarrativeSection from '@/components/dsr/NarrativeSection';
import ShiftLogSection from '@/components/dsr/ShiftLogSection';
import ActionItemsSection from '@/components/dsr/ActionItemsSection';

interface SectionDef {
  id: string;
  label: string;
  hasData: (d: DSRDetailData) => boolean;
}

// Mirrors DSRDetailPage SECTIONS minus annotations (Phase 2 per AC 12.1.8).
const SECTIONS: SectionDef[] = [
  { id: 'panel-section-daily-setup', label: 'Daily Setup', hasData: (d) => !!d.dailySetup },
  { id: 'panel-section-workforce', label: 'Workforce', hasData: (d) => d.personnel.length > 0 },
  { id: 'panel-section-activities', label: 'Activities', hasData: (d) => d.activities.length > 0 },
  { id: 'panel-section-equipment', label: 'Equipment', hasData: (d) => d.equipment.length > 0 },
  { id: 'panel-section-hse-observations', label: 'HSE Observations', hasData: (d) => d.hseObservations.length > 0 },
  { id: 'panel-section-permits', label: 'Permits', hasData: (d) => d.permits.length > 0 },
  { id: 'panel-section-shift-brief', label: 'Shift Brief', hasData: () => true },
  { id: 'panel-section-safety-meetings', label: 'Safety Meetings', hasData: (d) => d.meetings.length > 0 },
  { id: 'panel-section-jsa-evaluations', label: 'JSA Evaluations', hasData: (d) => d.jsaEvaluations.length > 0 },
  { id: 'panel-section-swa-events', label: 'SWA Events', hasData: (d) => d.swaEvents.length > 0 },
  { id: 'panel-section-safety-events', label: 'Safety Events', hasData: (d) => d.safetyEvents.length > 0 },
  {
    id: 'panel-section-forecasting',
    label: 'Forecasting',
    hasData: (d) =>
      d.forecastedActivities.length > 0 || d.outstandingItems.length > 0 || !!d.forecastingSummary,
  },
  { id: 'panel-section-shift-log', label: 'Shift Log', hasData: (d) => d.shiftLogs.length > 0 },
  { id: 'panel-section-narrative', label: 'Shift Narrative (legacy)', hasData: (d) => !!d.narrative?.content },
  { id: 'panel-section-action-items', label: 'Action Items', hasData: (d) => d.actionItems.length > 0 },
];

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  draft: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Draft' },
  submitted: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Submitted' },
  reviewed: { className: 'bg-mozaic-teal/15 text-mozaic-teal', label: 'Reviewed' },
  flagged: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Flagged' },
  amended: { className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Amended' },
};

const RISK_BADGE: Record<string, { className: string; label: string }> = {
  low: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Low' },
  medium: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Medium' },
  high: { className: 'bg-mozaic-red/15 text-mozaic-red', label: 'High' },
  critical: { className: 'bg-mozaic-red/20 text-mozaic-red font-bold', label: 'Critical' },
};

interface Props {
  dsrId: string | null;
  onClose: () => void;
}

// Read-only DSR detail rendered inside a right-side Sheet. Reuses every
// section component from DSRDetailPage. Annotations and edit/review
// affordances are intentionally omitted — the "Open full page" link is
// the path to those (and the print/share fallback per AC 12.1.8).
export default function DSRDetailPanel({ dsrId, onClose }: Props) {
  const open = !!dsrId;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dsr_detail', dsrId],
    queryFn: () => getDSRDetail(dsrId!),
    enabled: !!dsrId,
    staleTime: 5 * 60 * 1000,
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [jumpValue, setJumpValue] = useState<string>('');

  // Reset jump-select + scroll position whenever the dsrId changes so the
  // user always lands at the top of a freshly-opened report.
  useEffect(() => {
    setJumpValue('');
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [dsrId]);

  const scrollToSection = (id: string) => {
    setJumpValue(id);
    const root = scrollRef.current;
    if (!root) return;
    const el = root.querySelector('#' + id);
    if (el && el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:!max-w-2xl md:!max-w-3xl lg:!max-w-[64vw] xl:!max-w-[58vw]"
      >
        {isLoading && <PanelSkeleton />}
        {isError && (
          <PanelError
            dsrId={dsrId}
            onClose={onClose}
          />
        )}
        {!isLoading && !isError && data && (
          <PanelContent
            data={data}
            scrollRef={scrollRef}
            jumpValue={jumpValue}
            onJump={scrollToSection}
            onClose={onClose}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Subviews ───────────────────────────────────────────────

function PanelSkeleton() {
  return (
    <>
      <div className="space-y-2 border-b px-5 py-4">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    </>
  );
}

function PanelError({ dsrId, onClose }: { dsrId: string | null; onClose: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <p className="text-sm text-muted-foreground">
        Could not load this report.
      </p>
      <p className="text-xs text-muted-foreground">
        Try opening the full page view, which retries on its own.
      </p>
      {dsrId && (
        <Link
          to={`/reports/${dsrId}`}
          onClick={onClose}
          className="inline-flex items-center gap-1 text-sm text-mozaic-teal hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open full page
        </Link>
      )}
    </div>
  );
}

function PanelContent({
  data,
  scrollRef,
  jumpValue,
  onJump,
  onClose,
}: {
  data: DSRDetailData;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  jumpValue: string;
  onJump: (id: string) => void;
  onClose: () => void;
}) {
  const { report } = data;
  const project = report.projects;
  const submitter = report.users;
  const summaries = report.dsr_activities_summaries;
  // Composite-risk first (ARCH-030), legacy summary fallback.
  const riskLevel =
    report.overall_risk_level ?? summaries?.[0]?.overall_risk_level ?? null;
  const statusBadge = STATUS_BADGE[report.status] ?? STATUS_BADGE.draft;
  const riskBadge = riskLevel ? RISK_BADGE[riskLevel] : null;
  const navSections = SECTIONS.filter((s) => s.hasData(data));

  return (
    <>
      <div className="space-y-2 border-b px-5 py-3 pr-12">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <h2 className="text-base font-semibold leading-tight">
            {project?.name ?? 'Unknown Project'}
          </h2>
          <span className="text-sm text-muted-foreground">
            {format(new Date(report.report_date), 'MMM d, yyyy')}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge className="capitalize">{report.shift_type} Shift</Badge>
          <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
          {riskBadge && <Badge className={riskBadge.className}>{riskBadge.label} Risk</Badge>}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-x-3">
            {submitter && (
              <span>
                Submitted by {submitter.first_name} {submitter.last_name}
              </span>
            )}
            {report.submitted_at && (
              <span>{format(new Date(report.submitted_at), "MMM d 'at' HH:mm")}</span>
            )}
          </div>
          <Link
            to={`/reports/${report.id}`}
            onClick={onClose}
            className="inline-flex items-center gap-1 text-mozaic-teal hover:underline"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open full page
          </Link>
        </div>
        {navSections.length > 0 && (
          <div className="pt-1">
            <Select value={jumpValue} onValueChange={(v) => v && onJump(v)}>
              <SelectTrigger className="h-8 w-full">
                <SelectValue placeholder="Jump to section…" />
              </SelectTrigger>
              <SelectContent>
                {navSections.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
        <div className="space-y-6">
          <RiskScoreCard report={report} canRecompute={false} />

          <section id="panel-section-daily-setup">
            <DailySetupSection data={data.dailySetup} />
          </section>
          <section id="panel-section-workforce">
            <WorkforceSection data={data.personnel} />
          </section>
          <section id="panel-section-activities">
            <ActivitiesSection data={data.activities} />
          </section>
          <section id="panel-section-equipment">
            <EquipmentSection data={data.equipment} />
          </section>
          <section id="panel-section-hse-observations">
            <HSEObservationsSection
              data={data.hseObservations}
              photos={data.photoAttachments}
            />
          </section>
          <section id="panel-section-permits">
            <PermitsSection data={data.permits} />
          </section>
          <section id="panel-section-shift-brief">
            <ShiftBriefCard
              dsrId={report.id}
              tenantId={report.tenant_id}
              reportStatus={report.status}
              mode="detail"
            />
          </section>
          <section id="panel-section-safety-meetings">
            <SafetyMeetingsSection data={data.meetings} />
          </section>
          <section id="panel-section-jsa-evaluations">
            <JSAEvaluationsSection
              data={data.jsaEvaluations}
              photos={data.photoAttachments}
            />
          </section>
          <section id="panel-section-swa-events">
            <SWAEventsSection
              data={data.swaEvents}
              photos={data.photoAttachments}
            />
          </section>
          <section id="panel-section-safety-events">
            <SafetyEventsSection
              data={data.safetyEvents}
              photos={data.photoAttachments}
            />
          </section>
          <section id="panel-section-forecasting">
            <ForecastingSection
              forecastedActivities={data.forecastedActivities}
              outstandingItems={data.outstandingItems}
              summary={data.forecastingSummary}
            />
          </section>
          <section id="panel-section-shift-log">
            <ShiftLogSection data={data.shiftLogs} />
          </section>
          <section id="panel-section-narrative">
            <NarrativeSection data={data.narrative} />
          </section>
          <section id="panel-section-action-items">
            <ActionItemsSection data={data.actionItems} />
          </section>
        </div>
        <div className="mt-6 border-t pt-4">
          <Link
            to={`/reports/${report.id}`}
            onClick={onClose}
            className={buttonVariants({ variant: 'outline', size: 'sm', className: 'w-full' })}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Open Full Page View
          </Link>
        </div>
      </div>
    </>
  );
}
